import { Application, Feature, FeatureTarget, Functionality } from '@frontend/functionality/types';
import { IntlShape } from 'react-intl';

export function getFuncionalityFullName(functionality: Functionality): string {
    return `${functionality.name}: ${functionality.type}_${functionality.application}_${functionality.feature} - ${functionality.target}`;
}

export function getFunctionalityDisplayName(intl: IntlShape, functionality: Functionality): string {
    let name = guesFunctionalityTranslation(intl, functionality);
    if (name) return name;
    name = getDisplayNameFromApplicationFeature(intl, functionality);
    if (name) return name;
    return functionality.name.replace(/_/g, ' ');
}
export default getFunctionalityDisplayName;

function getDisplayNameFromApplicationFeature(intl: IntlShape, functionality: Functionality): string | null {
    const { application, feature, target } = functionality;
    switch (application) {
        case Application.ADDRESS_BOOK:
            return getAddressBookDisplayName(intl, feature);
        case Application.ADMIN:
            return getAdminDisplayName(intl, feature);
        case Application.ANALYTICS:
            return getAnalyticsDisplayName(intl, feature);
        case Application.AUTHORIZATION:
            return getAuthorizationDisplayName(intl, feature);
        case Application.CATALOGUE:
            return getCatalogueDisplayName(intl, feature);
        case Application.CONSTRAINTS:
            return getConstraintsDisplayName(intl, feature);
        case Application.HARDWARE:
            return getHardwareDisplayName(intl, feature);
        case Application.ITSME:
            return getItsmeDisplayName(intl, feature);
        case Application.LAUNDRY:
            return getLaundryDisplayName(intl, feature);
        case Application.LENDING:
            return getLendingDisplayName(intl, feature, target);
        case Application.NIVALIZ:
            return getNivalizDisplayName(intl, feature);
        case Application.NOTIFICATIONS:
            return getNotificationsDisplayName(intl, feature);
        case Application.ORGANIZATION:
            return getOrganizationDisplayName(intl, feature);
        case Application.PUDO:
            return getPudoDisplayName(intl, feature, target);
        case Application.REQUIREMENTS:
            return getRequirementsDisplayName(intl, feature);
        case Application.SHOP:
            return getShopDisplayName(intl, feature);
        case Application.SPOTS:
            return getSpotsDisplayName(intl, feature, target);
        case Application.USERS:
            return getUsersDisplayName(intl, feature);
        case Application.VENDING:
            return getVendingDisplayName(intl, feature, target);
    }
    return null;
}

function getOrganizationDisplayName(intl: IntlShape, feature: Feature): string | null {
    switch (feature) {
        case Feature.INVITES:
            return intl.formatMessage({
                id: 'functionality.organization.invites',
                description: 'Navbar label for the organization application invites feature',
                defaultMessage: 'Invites'
            });
    }
    return null;
}

function getVendingDisplayName(intl: IntlShape, feature: Feature, target: FeatureTarget): string | null {
    switch (feature) {
        case Feature.STOCK:
            return intl.formatMessage({ id: 'functionality.vending.stock', defaultMessage: 'Stock' });
        case Feature.INBOUND_TRANSACTION:
            if (target === FeatureTarget.PLATFORM)
                return intl.formatMessage({ id: 'functionality.vending.inbound_transaction.platform', defaultMessage: 'Replenishments' });
            if (target === FeatureTarget.KIOSK)
                return intl.formatMessage({ id: 'functionality.vending.inbound_transaction.kiosk', defaultMessage: 'Replenish products' });
            return null;
        case Feature.OUTBOUND_TRANSACTION:
            return intl.formatMessage({ id: 'functionality.vending.outbound_transaction', defaultMessage: 'Consumptions' });
        case Feature.DASHBOARD:
            return intl.formatMessage({ id: 'functionality.vending.dashboard', defaultMessage: 'Dashboard' });
    }
    return null;
}

function getUsersDisplayName(intl: IntlShape, feature: Feature): string | null {
    switch (feature) {
        case Feature.INVITES:
            return intl.formatMessage({ id: 'functionality.users.invites', defaultMessage: 'Invites' });
        case Feature.USERS:
            return intl.formatMessage({ id: 'functionality.users', defaultMessage: 'Users' });
        case Feature.USER_GROUPS:
            return intl.formatMessage({ id: 'functionality.user_groups', defaultMessage: 'User Groups' });
        case Feature.CERTIFICATES:
            return intl.formatMessage({ id: 'functionality.users.certificates', defaultMessage: 'Certificates' });
    }
    return null;
}

function getAuthorizationDisplayName(intl: IntlShape, feature: Feature): string | null {
    switch (feature) {
        case Feature.ROLES:
            return intl.formatMessage({
                id: 'functionality.authorization.roles',
                description: 'Navbar label for the authorization application roles feature',
                defaultMessage: 'Roles'
            });
        case Feature.PERMISSIONS:
            return intl.formatMessage({
                id: 'functionality.authorization.permissions',
                description: 'Navbar label for the authorization application permissions feature',
                defaultMessage: 'Permissions'
            });
    }
    return null;
}

function getSpotsDisplayName(intl: IntlShape, feature: Feature, target: FeatureTarget): string | null {
    switch (feature) {
        case Feature.SPOTS:
            return intl.formatMessage({ id: 'functionality.spots', defaultMessage: 'distriSPOTs' });
        case Feature.LAYOUT:
            if (target === FeatureTarget.PLATFORM) return intl.formatMessage({ id: 'functionality.spots.layout.platform', defaultMessage: 'Content' });
            if (target === FeatureTarget.KIOSK) return intl.formatMessage({ id: 'functionality.spots.layout.kiosk', defaultMessage: 'Manage distriSPOT' });
            return null;
        case Feature.MODULES:
            return intl.formatMessage({ id: 'functionality.spots.modules', defaultMessage: 'Modules' });
        case Feature.SLOTS:
            return intl.formatMessage({ id: 'functionality.spots.slots', defaultMessage: 'SLOTs' });
    }
    return null;
}

function getShopDisplayName(intl: IntlShape, feature: Feature): string | null {
    switch (feature) {
        case Feature.RETURN_TRANSACTION:
            return intl.formatMessage({ id: 'functionality.shop.return_transaction', defaultMessage: 'Return products' });
        case Feature.OUTBOUND_TRANSACTION:
            return intl.formatMessage({ id: 'functionality.shop.outbound_transaction', defaultMessage: 'Shop' });
        case Feature.ORDERS:
            return intl.formatMessage({ id: 'functionality.shop.orders', defaultMessage: 'Orders' });
    }
    return null;
}

function getRequirementsDisplayName(intl: IntlShape, feature: Feature): string | null {
    switch (feature) {
        case Feature.REQUIREMENTS:
            return intl.formatMessage({ id: 'functionality.requirements', defaultMessage: 'Requirements' });
    }
    return null;
}

function getPudoDisplayName(intl: IntlShape, feature: Feature, target: FeatureTarget): string | null {
    switch (feature) {
        case Feature.OUTBOUND_TRANSACTION:
            if (target === FeatureTarget.KIOSK) return intl.formatMessage({ id: 'functionality.pudo.outbound_transaction.kiosk', defaultMessage: 'Drop-off' });
            if (target === FeatureTarget.PLATFORM)
                return intl.formatMessage({ id: 'functionality.pudo.outbound_transaction.platform', defaultMessage: 'Awaiting drop-off' });
            if (target === FeatureTarget.API)
                return intl.formatMessage({ id: 'functionality.pudo.outbound_transaction.api', defaultMessage: 'Create transaction' });
            return null;
        case Feature.INBOUND_TRANSACTION:
            if (target === FeatureTarget.KIOSK) return intl.formatMessage({ id: 'functionality.pudo.inbound_transaction.kiosk', defaultMessage: 'Pick-up' });
            if (target === FeatureTarget.PLATFORM)
                return intl.formatMessage({ id: 'functionality.pudo.inbound_transaction.platform', defaultMessage: 'Awaiting pick-up' });
            return null;
        case Feature.DASHBOARD:
            return intl.formatMessage({ id: 'functionality.pudo.dashboard', defaultMessage: 'Dashboard' });
        case Feature.TRANSACTIONS:
            if (target === FeatureTarget.MOBILE)
                return intl.formatMessage({ id: 'functionality.pudo.transactions.mobile', defaultMessage: 'Create transaction' });
            return intl.formatMessage({ id: 'functionality.pudo.transactions', defaultMessage: 'Transactions' });
        case Feature.OPEN_DROP_OFF:
            return intl.formatMessage({ id: 'functionality.pudo.open_drop_off', defaultMessage: 'Open drop-off' });
        case Feature.ORDER_TRANSACTION:
            return intl.formatMessage({
                id: 'functionality.pudo.order_transaction',
                description: 'Name of the PuDo functionality that converts shop orders into transactions',
                defaultMessage: 'Order transaction'
            });
    }
    return null;
}

function getNotificationsDisplayName(intl: IntlShape, feature: Feature): string | null {
    switch (feature) {
        case Feature.NOTIFICATIONS:
            return intl.formatMessage({ id: 'functionality.notifications', defaultMessage: 'Notifications' });
        case Feature.TEMPLATES:
            return intl.formatMessage({ id: 'functionality.notifications.templates', defaultMessage: 'Templates' });
    }
    return null;
}

function getLendingDisplayName(intl: IntlShape, feature: Feature, target: FeatureTarget): string | null {
    switch (feature) {
        case Feature.INVENTORY:
            return intl.formatMessage({ id: 'functionality.lending.inventory', defaultMessage: 'Inventory' });
        case Feature.OUTBOUND_TRANSACTION:
            if (target === FeatureTarget.KIOSK) return intl.formatMessage({ id: 'functionality.lending.outbound_transaction.kiosk', defaultMessage: 'Shop' });
            if (target === FeatureTarget.PLATFORM)
                return intl.formatMessage({ id: 'functionality.lending.outbound_transaction.platform', defaultMessage: 'Utilizations' });
            return null;
        case Feature.INBOUND_TRANSACTION:
        case Feature.RETURN_TRANSACTION:
            return intl.formatMessage({ id: 'functionality.lending.inbound_transaction', defaultMessage: 'Return' });
        case Feature.CERTIFICATES:
            return intl.formatMessage({ id: 'functionality.lending.certificates', defaultMessage: 'Certificates' });
        case Feature.REVIEW:
            return intl.formatMessage({ id: 'functionality.lending.review', defaultMessage: 'Review returns' });
    }
    return null;
}

function getLaundryDisplayName(intl: IntlShape, feature: Feature): string | null {
    switch (feature) {
        case Feature.OUTBOUND_TRANSACTION:
            return intl.formatMessage({ id: 'functionality.laundry.outbound_transaction', defaultMessage: 'Send dirty laundry' });
    }
    return null;
}

function getItsmeDisplayName(intl: IntlShape, feature: Feature): string | null {
    switch (feature) {
        case Feature.QR_CODE:
            return intl.formatMessage({ id: 'functionality.itsme.qr_code', defaultMessage: 'Login with itsme' });
    }
    return null;
}

function getHardwareDisplayName(intl: IntlShape, feature: Feature): string | null {
    switch (feature) {
        case Feature.ACCESS_CONTROLLERS:
            return intl.formatMessage({ id: 'functionality.hardware.access_controllers', defaultMessage: 'Access controllers' });
        case Feature.GATES:
            return intl.formatMessage({ id: 'functionality.hardware.gates', defaultMessage: 'Gates' });
        case Feature.INFRASTRUCTURE:
            return intl.formatMessage({ id: 'functionality.hardware.infrastructure', defaultMessage: 'Infrastructure' });
        case Feature.DASHBOARD:
            return intl.formatMessage({ id: 'functionality.hardware.dashboard', defaultMessage: 'Dashboard' });
    }
    return null;
}

function getConstraintsDisplayName(intl: IntlShape, feature: Feature): string | null {
    switch (feature) {
        case Feature.CONSTRAINTS:
            return intl.formatMessage({ id: 'functionality.constraints', defaultMessage: 'Constraints' });
    }
    return null;
}

function getCatalogueDisplayName(intl: IntlShape, feature: Feature): string | null {
    switch (feature) {
        case Feature.CATEGORIES:
            return intl.formatMessage({
                id: 'functionality.catalogue.categories',
                defaultMessage: 'Categories',
                description: 'Display name for the catalogue categories feature'
            });
        case Feature.PRODUCTS:
            return intl.formatMessage({
                id: 'functionality.catalogue.products',
                defaultMessage: 'Products',
                description: 'Display name for the catalogue products feature'
            });
        case Feature.KITS:
            return intl.formatMessage({
                id: 'functionality.catalogue.kits',
                defaultMessage: 'Kits',
                description: 'Display name for the catalogue product kits feature'
            });
        case Feature.ATTRIBUTES:
            return intl.formatMessage({
                id: 'functionality.catalogue.attributes',
                defaultMessage: 'Attributes',
                description: 'Display name for the catalogue attributes feature'
            });
    }
    return null;
}

function getAnalyticsDisplayName(intl: IntlShape, feature: Feature): string | null {
    switch (feature) {
        case Feature.INSIGHTS:
            return intl.formatMessage({ id: 'functionality.analytics.insights', defaultMessage: 'Insights' });
        case Feature.REPORTS:
            return intl.formatMessage({ id: 'functionality.analytics.reports', defaultMessage: 'Reports' });
    }
    return null;
}

function getAdminDisplayName(intl: IntlShape, feature: Feature): string | null {
    switch (feature) {
        case Feature.ADMIN:
            return intl.formatMessage({ id: 'functionality.admin', defaultMessage: 'Admin' });
    }
    return null;
}

function getAddressBookDisplayName(intl: IntlShape, feature: Feature): string | null {
    switch (feature) {
        case Feature.CONTACTS:
            return intl.formatMessage({ id: 'functionality.address_book.contacts', defaultMessage: 'Contacts' });
    }
    return null;
}

function getNivalizDisplayName(intl: IntlShape, feature: Feature): string | null {
    switch (feature) {
        case Feature.DASHBOARD:
            return intl.formatMessage({
                id: 'functionality.nivaliz.dashboard',
                description: 'Navbar label for the Nivaliz dashboard feature',
                defaultMessage: 'Dashboard'
            });
        case Feature.INBOUND_TRANSACTION:
            return intl.formatMessage({ id: 'feature.nivaliz.coffins', defaultMessage: 'Coffins' });
        case Feature.OUTBOUND_TRANSACTION:
            return intl.formatMessage({ id: 'feature.nivaliz.urns', defaultMessage: 'Urns' });
        case Feature.TRANSACTIONS:
            return intl.formatMessage({
                id: 'functionality.nivaliz.transactions',
                description: 'Navbar label for the Nivaliz transactions feature',
                defaultMessage: 'Transactions'
            });
        case Feature.DECEASED:
            return intl.formatMessage({
                id: 'functionality.nivaliz.deceased',
                description: 'Navbar label for the Nivaliz deceased feature',
                defaultMessage: 'Deceased'
            });
    }
    return null;
}

function guesFunctionalityTranslation(intl: IntlShape, functionality: Functionality): string | null {
    switch (functionality.name.toLocaleLowerCase()) {
        case 'coffins':
            return intl.formatMessage({ id: 'feature.nivaliz.coffins', defaultMessage: 'Coffins' });
        case 'urns':
            return intl.formatMessage({ id: 'feature.nivaliz.urns', defaultMessage: 'Urns' });
        case 'fablab':
            return intl.formatMessage({
                id: 'functionality.fablab',
                defaultMessage: 'FabLab',
                description: 'Display name for the FabLab functionality'
            });
        case 'send clean laundry':
            return intl.formatMessage({ id: 'functionality.send_clean_laundry', defaultMessage: 'Send clean laundry' });
        case 'send dirty laundry':
            return intl.formatMessage({ id: 'functionality.send_dirty_laundry', defaultMessage: 'Send dirty laundry' });
        case 'receive dirty laundry':
            return intl.formatMessage({ id: 'functionality.receive_dirty_laundry', defaultMessage: 'Receive dirty laundry' });
        case 'ready to receive':
            return intl.formatMessage({ id: 'functionality.ready_to_receive', defaultMessage: 'Ready to receive' });
        case 'ready to ship':
            return intl.formatMessage({ id: 'functionality.ready_to_ship', defaultMessage: 'Ready to ship' });
        case 'shipments':
            return intl.formatMessage({ id: 'functionality.shipments', defaultMessage: 'Shipments' });
        case 'inventory':
            return intl.formatMessage({ id: 'functionality.inventory', defaultMessage: 'Inventory' });
        case 'items':
            return intl.formatMessage({ id: 'functionality.items', defaultMessage: 'Items' });
        case 'shipping labels':
            return intl.formatMessage({ id: 'functionality.shipping_labels', defaultMessage: 'Shipping labels' });
        case 'open scanner':
            return intl.formatMessage({ id: 'functionality.open_scanner', defaultMessage: 'Open scanner' });
        case 'scanner':
            return intl.formatMessage({ id: 'functionality.scanner', defaultMessage: 'Scanner' });
        case 'insights':
            return intl.formatMessage({ id: 'functionality.insights', defaultMessage: 'Insights' });
        case 'reports':
            return intl.formatMessage({ id: 'functionality.reports', defaultMessage: 'Reports' });
        case 'requirements':
            return intl.formatMessage({ id: 'functionality.requirements', defaultMessage: 'Requirements' });
        case 'infrastructure':
            return intl.formatMessage({ id: 'functionality.infrastructure', defaultMessage: 'Infrastructure' });
        case 'gates':
            return intl.formatMessage({ id: 'functionality.gates', defaultMessage: 'Gates' });
        case 'access controllers':
            return intl.formatMessage({ id: 'functionality.access_controllers', defaultMessage: 'Access controllers' });
        case 'locations':
            return intl.formatMessage({ id: 'functionality.locations', defaultMessage: 'Locations' });
        case 'constraints':
            return intl.formatMessage({ id: 'functionality.constraints', defaultMessage: 'Constraints' });
        case 'attributes':
            return intl.formatMessage({ id: 'functionality.attributes', defaultMessage: 'Attributes' });
        case 'categories':
            return intl.formatMessage({ id: 'functionality.categories', defaultMessage: 'Categories' });
        case 'products':
            return intl.formatMessage({ id: 'functionality.products', defaultMessage: 'Products' });
        case 'contacts':
            return intl.formatMessage({ id: 'functionality.contacts', defaultMessage: 'Contacts' });
        case 'replenishments':
            return intl.formatMessage({ id: 'functionality.replenishments', defaultMessage: 'Replenishments' });
        case 'consumptions':
            return intl.formatMessage({ id: 'functionality.consumptions', defaultMessage: 'Consumptions' });
        case 'admin':
            return intl.formatMessage({ id: 'functionality.admin', defaultMessage: 'Admin' });
        case 'users':
            return intl.formatMessage({ id: 'functionality.users', defaultMessage: 'Users' });
        case 'user groups':
            return intl.formatMessage({ id: 'functionality.user_groups', defaultMessage: 'User Groups' });
        case 'certificates':
            return intl.formatMessage({ id: 'functionality.certificates', defaultMessage: 'Certificates' });
        case 'invites':
            return intl.formatMessage({ id: 'functionality.invites', defaultMessage: 'Invites' });
        case 'utilizations':
            return intl.formatMessage({ id: 'functionality.utilizations', defaultMessage: 'Utilizations' });
        case 'stock':
            return intl.formatMessage({ id: 'functionality.stock', defaultMessage: 'Stock' });
        case 'stock requirements':
            return intl.formatMessage({ id: 'functionality.stock_requirements', defaultMessage: 'Stock requirements' });
        case 'inventory':
            return intl.formatMessage({ id: 'functionality.inventory', defaultMessage: 'Inventory' });
        case 'transactions':
            return intl.formatMessage({ id: 'functionality.transactions', defaultMessage: 'Transactions' });
        case 'create transaction':
            return intl.formatMessage({ id: 'functionality.create_transaction', defaultMessage: 'Create transaction' });
        case 'templates':
            return intl.formatMessage({ id: 'functionality.templates', defaultMessage: 'Templates' });
        case 'notifications':
            return intl.formatMessage({ id: 'functionality.notifications', defaultMessage: 'Notifications' });
        case 'pick-up':
        case 'pick up':
        case 'authenticated pick up':
        case 'authenticated pick-up':
            return intl.formatMessage({ id: 'functionality.pick_up', defaultMessage: 'Pick-up' });
        case 'courier dropoff':
        case 'courier drop-off':
            return intl.formatMessage({ id: 'functionality.courier_drop_off', defaultMessage: 'Courier drop-off' });
        case 'internal dropoff':
        case 'internal drop-off':
            return intl.formatMessage({ id: 'functionality.internal_drop_off', defaultMessage: 'Internal drop-off' });
        case 'drop-off':
        case 'drop off':
        case 'authenticated drop off':
        case 'authenticated drop-off':
            return intl.formatMessage({ id: 'functionality.drop_off', defaultMessage: 'Drop-off' });
        case 'open drop-off':
        case 'open drop off':
        case 'authenticated open drop off':
        case 'authenticated open drop-off':
            return intl.formatMessage({ id: 'functionality.open_drop_off', defaultMessage: 'Open drop-off' });
        case 'hand off':
        case 'hand-off':
        case 'hand_off':
            return intl.formatMessage({ id: 'functionality.hand_off', defaultMessage: 'Hand off' });
        case 'awaiting pick-up':
        case 'awaiting pick up':
            return intl.formatMessage({ id: 'functionality.awaiting_pick_up', defaultMessage: 'Awaiting pick-up' });
        case 'awaiting drop-off':
        case 'awaiting drop off':
            return intl.formatMessage({ id: 'functionality.awaiting_drop_off', defaultMessage: 'Awaiting drop-off' });
        case 'shop':
            return intl.formatMessage({ id: 'functionality.shop', defaultMessage: 'Shop' });
        case 'manage distrispot':
            return intl.formatMessage({ id: 'functionality.manage_distrispot', defaultMessage: 'Manage distriSPOT' });
        case 'distrispot':
            return intl.formatMessage({ id: 'functionality.distrispot', defaultMessage: 'distriSPOT' });
        case 'modules':
            return intl.formatMessage({ id: 'functionality.modules', defaultMessage: 'Modules' });
        case 'slots':
            return intl.formatMessage({ id: 'functionality.slots', defaultMessage: 'SLOTs' });
        case 'layout':
            return intl.formatMessage({ id: 'functionality.layout', defaultMessage: 'Layout' });
        case 'replenish products':
        case 'refill products':
            return intl.formatMessage({ id: 'functionality.replenish_products', defaultMessage: 'Replenish products' });
        case 'return products':
            return intl.formatMessage({ id: 'functionality.return_products', defaultMessage: 'Return products' });
        case 'return':
            return intl.formatMessage({ id: 'functionality.return', defaultMessage: 'Return' });
        case 'replenishes':
        case 'refills':
            return intl.formatMessage({ id: 'functionality.replenishes', defaultMessage: 'Replenishes' });
        case 'content':
            return intl.formatMessage({ id: 'functionality.content', defaultMessage: 'Content' });
        case 'login with itsme':
            return intl.formatMessage({ id: 'functionality.login_with_itsme', defaultMessage: 'Login with itsme' });
    }
    return null;
}
